// Fahrerabrechnung (Storyline 6): Was ein Fahrer an der Tür kassiert oder für Leergut ausgezahlt hat, hält die
// Zustellung fest (Modul Verkauf, sales.delivery.confirm). Am Ende der Tour wird je Fahrzeug abgerechnet:
//   finance.driverCash.open      was noch nicht abgerechnet ist – Soll bar und Soll Karte je Fahrzeug
//   finance.driverCash.submit    Abgabe: abgegebenes Bargeld gegen das Soll, Differenz nur mit Grund
//   finance.driverCash.approve   Freigabe durch eine ZWEITE Person: bucht die Zahlungen auf die Kundenkonten
//                                (gegen die Rechnung der Lieferung, sonst als Guthaben) und die Kassendifferenz
//   finance.driverCash.reject    weist eine eingereichte Abrechnung zurück – die Zustellungen sind wieder offen
//   finance.driverCash.list | get   Verlauf und Einzelheiten
// Alle Beträge in Cent. Gezähltes Geld gehört nicht dem Assistenten: submit ist `assistant: 'never'`, die Freigabe
// bucht mehrere Zahlungen auf einmal und ist `'opt-in'`.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zLocationRef, zNote, zPartnerRef, zReason } from './common'

const SETTLE = 'finance.driverCash.settle'
const APPROVE = 'finance.driverCash.approve'

export const DRIVER_CASH_STATUSES = ['submitted', 'approved'] as const
export const DRIVER_CASH_STATUS_LABELS: Record<(typeof DRIVER_CASH_STATUSES)[number], string> = { submitted: 'Zur Freigabe', approved: 'Freigegeben' }

const zPerson = z.object({ id: zId, name: z.string() })

/** Eine Zustellung mit dem, was an der Tür kassiert oder ausgezahlt wurde. */
export const zDriverCashStop = z.object({
  id: zId,
  deliveryId: zId,
  deliveryNumber: z.string().nullable(),
  customer: zPartnerRef,
  tourDate: z.string(),
  /** an der Tür fällig: übergebene Ware mit Pfand abzüglich Leergutgutschrift */
  dueCents: z.int(),
  cashCents: z.int(),
  cardCents: z.int(),
  /** bar ausgezahlt für Leergut */
  payoutCents: z.int(),
  collectedBy: zPerson,
  collectedAt: z.date(),
  /** nach der Freigabe: „RE-2026-000123 ausgeglichen“ bzw. „Guthaben ohne Zuordnung“ */
  bookedText: z.string().nullable(),
})

const zExpected = z.object({
  /** Soll bar: bar kassiert − bar ausgezahlt */
  expectedCashCents: z.int(),
  /** Soll Karte: Summe der Kartenbelege */
  expectedCardCents: z.int(),
  collectedCashCents: z.int(),
  payoutCents: z.int(),
  stopCount: z.int(),
})

export const driverCashOpen = defineContract({
  name: 'finance.driverCash.open',
  title: 'Offene Fahrerkassen anzeigen',
  description: 'Je Fahrzeug, was an der Tür kassiert und noch nicht abgerechnet ist: Soll bar, Soll Karte und die Zustellungen dazu.',
  llm: {
    description:
      'Nennt je Fahrzeug die Zustellungen, bei denen der Fahrer bar oder mit Karte kassiert bzw. bar ' +
      'ausgezahlt hat und die noch in keiner Fahrerabrechnung stehen – mit dem Soll in Cent (bar = kassiert − ' +
      'ausgezahlt, Karte = Summe der Kartenbelege). Sichtbar sind Fahrzeuge, an denen der Aufrufer abrechnen oder ' +
      'freigeben darf. Verändert nichts; abgerechnet wird mit finance.driverCash.submit.',
    keywords: ['Fahrerkasse', 'Fahrerabrechnung', 'kassiert beim Kunden', 'Tour abrechnen', 'Bargeld Fahrer'],
  },
  input: z.strictObject({ locationId: zId.optional() }),
  output: z.object({
    vehicles: z.array(zExpected.extend({ location: zLocationRef, stops: z.array(zDriverCashStop), canSubmit: z.boolean() })),
    /** Abrechnungen, die auf die Freigabe warten (an den Standorten des Aufrufers) */
    pendingCount: z.int(),
  }),
  permission: SETTLE,
  risk: 'read',
  scope: 'any',
})

export const zDriverCashSettlement = zExpected.extend({
  id: zId,
  location: zLocationRef,
  businessDate: z.string(),
  status: z.enum(DRIVER_CASH_STATUSES),
  statusLabel: z.string(),
  /** abgegebenes Bargeld */
  handedInCents: z.int(),
  /** abgegeben − Soll bar (negativ = es fehlt Geld) */
  differenceCents: z.int(),
  differenceReason: z.string().nullable(),
  note: z.string().nullable(),
  submittedBy: zPerson,
  submittedAt: z.date(),
  approvedBy: zPerson.nullable(),
  approvedAt: z.date().nullable(),
  approvalNote: z.string().nullable(),
  version: z.int(),
})

export const driverCashList = defineContract({
  name: 'finance.driverCash.list',
  title: 'Fahrerabrechnungen anzeigen',
  description: 'Eingereichte und freigegebene Fahrerabrechnungen mit Soll, abgegebenem Bargeld und Differenz.',
  llm: {
    description:
      'Listet Fahrerabrechnungen (neueste zuerst): Fahrzeug, Tag der Abgabe, Soll bar und Karte, abgegebenes Bargeld, ' +
      'Differenz mit Grund, wer eingereicht und wer freigegeben hat. `status: "submitted"` zeigt nur, was auf die ' +
      'Freigabe wartet. Einzelheiten mit den Zustellungen liefert finance.driverCash.get.',
    keywords: ['Fahrerabrechnung', 'Fahrerkasse', 'Kassendifferenz Fahrer', 'zur Freigabe'],
  },
  input: z.strictObject({ status: z.enum(DRIVER_CASH_STATUSES).optional(), locationId: zId.optional(), limit: z.int().min(1).max(200).default(50) }),
  output: z.object({ rows: z.array(zDriverCashSettlement.extend({ canApprove: z.boolean(), canReject: z.boolean() })) }),
  permission: SETTLE,
  risk: 'read',
  scope: 'any',
})

export const driverCashGet = defineContract({
  name: 'finance.driverCash.get',
  title: 'Fahrerabrechnung anzeigen',
  description: 'Eine Fahrerabrechnung mit ihren Zustellungen und – nach der Freigabe – den gebuchten Zahlungen.',
  llm: {
    description: 'Liefert eine Fahrerabrechnung mit jeder Zustellung (Kunde, bar, Karte, Auszahlung) und nach der Freigabe dem Vermerk, wie die Zahlung gebucht wurde.',
    keywords: ['Fahrerabrechnung', 'Fahrerkasse'],
  },
  input: z.strictObject({ id: zId }),
  output: zDriverCashSettlement.extend({ stops: z.array(zDriverCashStop), canApprove: z.boolean(), canReject: z.boolean(), approveHint: z.string().nullable() }),
  permission: SETTLE,
  risk: 'read',
  scope: 'record',
})

export const driverCashSubmit = defineContract({
  name: 'finance.driverCash.submit',
  title: 'Fahrerkasse abrechnen',
  description: 'Rechnet ein Fahrzeug ab: abgegebenes Bargeld gegen das Soll aus den Zustellungen – eine Differenz nur mit Grund.',
  llm: {
    description: 'Reicht die Fahrerabrechnung eines Fahrzeugs ein. Nur für die Oberfläche – gezähltes Geld gibt der Mensch selbst ein.',
    keywords: ['Tour abrechnen', 'Fahrerkasse abgeben'],
  },
  input: z.strictObject({
    locationId: zId,
    /** abgegebenes Bargeld in Cent (0, wenn nur mit Karte kassiert wurde) */
    handedInCents: z.int({ error: 'Bitte das abgegebene Bargeld eingeben.' }).min(-10_000_000, { error: 'Der Betrag ist zu klein.' }).max(10_000_000, { error: 'Der Betrag ist zu groß.' }),
    differenceReason: z.string().trim().max(500, { error: 'Der Grund ist zu lang (höchstens 500 Zeichen).' }).nullish(),
    note: zNote,
  }),
  output: zDriverCashSettlement.extend({ message: z.string() }),
  permission: SETTLE,
  risk: 'financial',
  scope: (input) => [input.locationId],
  assistant: 'never',
})

export const driverCashApprove = defineContract({
  name: 'finance.driverCash.approve',
  title: 'Fahrerabrechnung freigeben',
  description: 'Gibt eine eingereichte Fahrerabrechnung frei und bucht die kassierten Beträge als Zahlungen auf die Kundenkonten.',
  llm: {
    description:
      'Gibt eine eingereichte Fahrerabrechnung frei (Vier-Augen-Regel: nicht, wer eingereicht oder selbst kassiert ' +
      'hat). Erst dadurch entstehen die Zahlungen: je Zustellung der bar bzw. mit Karte kassierte Betrag als ' +
      'Zahlungseingang des Kunden am Fahrzeug – ausgeglichen wird die Rechnung der Lieferung, sonst bleibt der Betrag ' +
      'als Guthaben, bis die Rechnung geschrieben ist; ausgezahltes Bargeld als Auszahlung gegen die ' +
      'Gutschrift des Kunden. Eine Differenz zwischen Soll und abgegebenem Bargeld erscheint im Buchungsjournal als ' +
      'Kassendifferenz. Nicht umkehrbar als Ganzes – einzelne Zahlungen storniert finance.payment.reverse.',
    keywords: ['Fahrerabrechnung freigeben', 'Fahrerkasse buchen', 'Tour abschließen'],
  },
  input: z.strictObject({ id: zId, note: zNote, expectedVersion: z.int().optional() }),
  output: zDriverCashSettlement.extend({ paymentCount: z.int(), message: z.string() }),
  permission: APPROVE,
  risk: 'financial',
  scope: 'record',
  assistant: 'opt-in',
})

export const driverCashReject = defineContract({
  name: 'finance.driverCash.reject',
  title: 'Fahrerabrechnung zurückweisen',
  description: 'Weist eine eingereichte Fahrerabrechnung mit Grund zurück – der Fahrer rechnet neu ab.',
  llm: {
    description:
      'Weist eine eingereichte, noch nicht freigegebene Fahrerabrechnung zurück (z. B. falsch gezählt). Gebucht war ' +
      'noch nichts; die Zustellungen stehen danach wieder als offen in finance.driverCash.open und werden neu ' +
      'abgerechnet. Der Grund steht im Protokoll.',
    keywords: ['Fahrerabrechnung zurückweisen', 'neu abrechnen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: z.object({ id: zId, message: z.string() }),
  permission: SETTLE,
  risk: 'write',
  scope: 'record',
})
